import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight, Award, Users, Globe, Brain, Calendar, Target } from 'lucide-react';
import { ZoneXLogo3D } from '../components/ZoneXLogo3D';
import * as THREE from 'three';
import { Link, useNavigate } from 'react-router-dom';

// Animated Red Particles for the hero section
const AboutParticles = () => {
    const particlesRef = useRef(null);

    useEffect(() => {
        if (!particlesRef.current) return;

        const canvas = document.createElement('canvas');
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
        particlesRef.current.appendChild(canvas);

        const ctx = canvas.getContext('2d');
        const particles = [];
        const particleCount = 50;

        // Create particles
        for (let i = 0; i < particleCount; i++) {
            particles.push({
                x: Math.random() * canvas.width,
                y: Math.random() * canvas.height,
                radius: Math.random() * 3 + 1,
                color: `rgba(252, 63, 63, ${Math.random() * 0.5 + 0.1})`,
                speedX: Math.random() * 1 - 0.5,
                speedY: Math.random() * 1 - 0.5
            });
        }

        // Animation function
        function animate() {
            requestAnimationFrame(animate);
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            particles.forEach(particle => {
                // Draw particle
                ctx.beginPath();
                ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
                ctx.fillStyle = particle.color;
                ctx.fill();

                // Update position
                particle.x += particle.speedX;
                particle.y += particle.speedY;

                // Boundary check
                if (particle.x < 0 || particle.x > canvas.width) particle.speedX *= -1;
                if (particle.y < 0 || particle.y > canvas.height) particle.speedY *= -1;
            });
        }

        // Handle window resize
        function handleResize() {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        }

        window.addEventListener('resize', handleResize);
        animate();

        return () => {
            window.removeEventListener('resize', handleResize);
            if (particlesRef.current) {
                particlesRef.current.removeChild(canvas);
            }
        };
    }, []);

    return <div ref={particlesRef} className="absolute inset-0 z-0 overflow-hidden"></div>;
};

// Timeline Animation component
const TimelineAnimation = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        if (!canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = canvas.clientWidth;
        canvas.height = canvas.clientHeight;

        // Updated timeline years
        const years = [2024, 2025, 2026, 2027];
        const milestones = [
            { year: 2024, text: "ZoneX Founded", color: "#FC3F3F" },
            { year: 2024.5, text: "First 10,000 Users", color: "#FC3F3F" },
            { year: 2025, text: "Mechanic Network", color: "#FC3F3F" },
            { year: 2025.5, text: "Innovation Award", color: "#FC3F3F" },
            { year: 2026, text: "Spare Parts Platform", color: "#FC3F3F" },
            { year: 2026.5, text: "Multi-City Expansion", color: "#FC3F3F" }
        ];

        // Animation variables
        let progress = 0;
        const animationSpeed = 0.005;

        function animate() {
            requestAnimationFrame(animate);
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Background
            ctx.fillStyle = "#111111";
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Draw timeline
            const timelineY = canvas.height / 2;
            const timelineStart = 50;
            const timelineEnd = canvas.width - 50;
            const timelineWidth = timelineEnd - timelineStart;

            // Base timeline
            ctx.beginPath();
            ctx.moveTo(timelineStart, timelineY);
            ctx.lineTo(timelineEnd, timelineY);
            ctx.strokeStyle = "#444444";
            ctx.lineWidth = 2;
            ctx.stroke();

            // Active timeline
            const activeLength = timelineWidth * Math.min(progress, 1);
            ctx.beginPath();
            ctx.moveTo(timelineStart, timelineY);
            ctx.lineTo(timelineStart + activeLength, timelineY);
            ctx.strokeStyle = "#FC3F3F";
            ctx.lineWidth = 3;
            ctx.stroke();

            // Year markers
            years.forEach((year, index) => {
                const x = timelineStart + (timelineWidth / (years.length - 1)) * index;

                // Draw year marker
                ctx.beginPath();
                ctx.arc(x, timelineY, 4, 0, Math.PI * 2);
                ctx.fillStyle = "#777777";
                ctx.fill();

                // Draw year text
                ctx.font = "16px Arial";
                ctx.fillStyle = "#AAAAAA";
                ctx.textAlign = "center";
                ctx.fillText(year.toString(), x, timelineY + 25);
            });

            // Draw milestones
            milestones.forEach((milestone) => {
                const yearRange = years[years.length - 1] - years[0];
                const milestonePosition = (milestone.year - years[0]) / yearRange;

                const x = timelineStart + (timelineWidth * milestonePosition);
                const milestoneProgress = Math.min(1, Math.max(0, (progress - milestonePosition + 0.05) * 10));

                if (milestoneProgress > 0) {
                    // Draw milestone marker
                    ctx.beginPath();
                    ctx.arc(x, timelineY, 6 * milestoneProgress, 0, Math.PI * 2);
                    ctx.fillStyle = milestone.color;
                    ctx.fill();

                    // Draw milestone text with fade in
                    ctx.globalAlpha = milestoneProgress;
                    ctx.font = "bold 14px Arial";
                    ctx.fillStyle = "#FFFFFF";
                    ctx.textAlign = "center";

                    // Alternate above/below timeline
                    const textY = milestone.year % 1 === 0 ? timelineY - 25 : timelineY + 40;
                    ctx.fillText(milestone.text, x, textY);
                    ctx.globalAlpha = 1;
                }
            });

            // Update progress
            progress += animationSpeed;
            if (progress > 1.2) progress = 0; // Loop animation
        }

        animate();

        // Handle window resize
        function handleResize() {
            canvas.width = canvas.clientWidth;
            canvas.height = canvas.clientHeight;
        }

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    return <canvas ref={canvasRef} className="w-full h-full"></canvas>;
};

// Vision Animation component with futuristic city
const VisionAnimation = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        if (!canvasRef.current) return;

        const scene = new THREE.Scene();

        // Camera setup
        const camera = new THREE.PerspectiveCamera(
            75,
            canvasRef.current.clientWidth / canvasRef.current.clientHeight,
            0.1,
            1000
        );
        camera.position.set(0, 5, 10);
        camera.lookAt(0, 0, 0);

        // Renderer setup
        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setSize(canvasRef.current.clientWidth, canvasRef.current.clientHeight);
        renderer.setClearColor(0x000000, 1);
        canvasRef.current.appendChild(renderer.domElement);

        // Lighting
        const ambientLight = new THREE.AmbientLight(0x404040, 1);
        scene.add(ambientLight);

        const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
        directionalLight.position.set(5, 10, 7.5);
        scene.add(directionalLight);

        // Red spotlight for ZoneX branding
        const redSpotlight = new THREE.SpotLight(0xFC3F3F, 2);
        redSpotlight.position.set(-5, 10, 0);
        redSpotlight.angle = Math.PI / 6;
        redSpotlight.penumbra = 0.3;
        scene.add(redSpotlight);

        // Create futuristic city
        const cityGroup = new THREE.Group();

        // Ground
        const groundGeometry = new THREE.PlaneGeometry(100, 100);
        const groundMaterial = new THREE.MeshStandardMaterial({
            color: 0x111111,
            roughness: 0.8,
        });
        const ground = new THREE.Mesh(groundGeometry, groundMaterial);
        ground.rotation.x = -Math.PI / 2;
        ground.position.y = -0.5;
        cityGroup.add(ground);

        // Grid lines on ground
        const gridHelper = new THREE.GridHelper(100, 100, 0xFC3F3F, 0x333333);
        gridHelper.position.y = -0.48;
        cityGroup.add(gridHelper);

        // Buildings
        function createBuilding(x, z, width, height, depth, color) {
            const buildingGeometry = new THREE.BoxGeometry(width, height, depth);
            const buildingMaterial = new THREE.MeshStandardMaterial({
                color,
                roughness: 0.7,
                metalness: 0.3,
            });
            const building = new THREE.Mesh(buildingGeometry, buildingMaterial);
            building.position.set(x, height / 2 - 0.5, z);

            // Add windows
            if (height > 2) {
                const floors = Math.floor(height / 1.2);
                const sides = Math.floor(width / 1.2);

                for (let i = 0; i < floors; i++) {
                    for (let j = 0; j < sides; j++) {
                        // Front windows
                        const windowGeometry = new THREE.PlaneGeometry(0.5, 0.5);
                        const windowMaterial = new THREE.MeshBasicMaterial({
                            color: 0x88CCFF,
                            transparent: true,
                            opacity: Math.random() > 0.3 ? 0.9 : 0
                        });
                        const windowMesh = new THREE.Mesh(windowGeometry, windowMaterial);
                        windowMesh.position.set(
                            (j - (sides - 1) / 2) * 0.8,
                            i * 1.2 - height / 2 + 1,
                            depth / 2 + 0.01
                        );

                        building.add(windowMesh);

                        // Back windows
                        const backWindow = windowMesh.clone();
                        backWindow.position.z = -depth / 2 - 0.01;
                        backWindow.rotation.y = Math.PI;
                        building.add(backWindow);
                    }
                }
            }

            return building;
        }

        // Create multiple buildings
        const buildingColors = [0x333333, 0x444444, 0x222222, 0x111111, 0x555555];
        for (let i = 0; i < 30; i++) {
            const x = (Math.random() - 0.5) * 80;
            const z = (Math.random() - 0.5) * 80;
            const width = Math.random() * 3 + 2;
            const height = Math.random() * 10 + 5;
            const depth = Math.random() * 3 + 2;
            const color = buildingColors[Math.floor(Math.random() * buildingColors.length)];

            const building = createBuilding(x, z, width, height, depth, color);
            cityGroup.add(building);
        }

        // Create roads
        const roadGeometry = new THREE.PlaneGeometry(80, 6);
        const roadMaterial = new THREE.MeshStandardMaterial({ color: 0x222222 });

        const road1 = new THREE.Mesh(roadGeometry, roadMaterial);
        road1.rotation.x = -Math.PI / 2;
        road1.position.y = -0.48;
        cityGroup.add(road1);

        const road2 = new THREE.Mesh(roadGeometry, roadMaterial);
        road2.rotation.x = -Math.PI / 2;
        road2.rotation.z = Math.PI / 2;
        road2.position.y = -0.48;
        cityGroup.add(road2);

        // Add cars
        function createCar(color) {
            const carGroup = new THREE.Group();

            // Car body
            const bodyGeometry = new THREE.BoxGeometry(1, 0.5, 2);
            const bodyMaterial = new THREE.MeshStandardMaterial({ color });
            const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
            body.position.y = 0.25;
            carGroup.add(body);

            // Car top
            const topGeometry = new THREE.BoxGeometry(0.8, 0.4, 1);
            const top = new THREE.Mesh(topGeometry, bodyMaterial);
            top.position.set(0, 0.7, -0.2);
            carGroup.add(top);

            // Wheels
            const wheelGeometry = new THREE.CylinderGeometry(0.2, 0.2, 0.1, 16);
            const wheelMaterial = new THREE.MeshStandardMaterial({ color: 0x333333 });

            const wheelPositions = [
                [-0.5, 0.2, 0.7], // Front left
                [0.5, 0.2, 0.7],  // Front right
                [-0.5, 0.2, -0.7], // Back left
                [0.5, 0.2, -0.7]  // Back right
            ];

            wheelPositions.forEach(pos => {
                const wheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
                wheel.rotation.z = Math.PI / 2;
                wheel.position.set(...pos);
                carGroup.add(wheel);
            });

            // Add headlights
            const headlightGeometry = new THREE.SphereGeometry(0.1, 8, 8);
            const headlightMaterial = new THREE.MeshBasicMaterial({ color: 0xFFFFCC });

            const leftHeadlight = new THREE.Mesh(headlightGeometry, headlightMaterial);
            leftHeadlight.position.set(-0.3, 0.3, 1);
            carGroup.add(leftHeadlight);

            const rightHeadlight = new THREE.Mesh(headlightGeometry, headlightMaterial);
            rightHeadlight.position.set(0.3, 0.3, 1);
            carGroup.add(rightHeadlight);

            return carGroup;
        }

        // Create moving cars
        const cars = [];
        const carColors = [0xFC3F3F, 0x4444FF, 0x44FF44, 0xFFAA00];

        for (let i = 0; i < 10; i++) {
            const car = createCar(carColors[i % carColors.length]);

            // Random road position
            const onVerticalRoad = Math.random() > 0.5;
            if (onVerticalRoad) {
                car.position.set(0, 0, (Math.random() - 0.5) * 60);
                car.rotation.y = Math.random() > 0.5 ? 0 : Math.PI;
            } else {
                car.position.set((Math.random() - 0.5) * 60, 0, 0);
                car.rotation.y = Math.random() > 0.5 ? Math.PI / 2 : -Math.PI / 2;
            }

            cars.push({
                mesh: car,
                speed: 0.1 + Math.random() * 0.1,
                onVerticalRoad
            });

            cityGroup.add(car);
        }

        // Create ZoneX logo as a hologram
        const createHologram = () => {
            const hologramGroup = new THREE.Group();

            // Base
            const baseGeometry = new THREE.CylinderGeometry(2, 2, 0.2, 32);
            const baseMaterial = new THREE.MeshStandardMaterial({
                color: 0xFC3F3F,
                emissive: 0xFC3F3F,
                emissiveIntensity: 0.5,
                transparent: true,
                opacity: 0.7
            });
            const base = new THREE.Mesh(baseGeometry, baseMaterial);
            hologramGroup.add(base);

            // Light beam
            const beamGeometry = new THREE.CylinderGeometry(0.1, 2, 6, 32, 1, true);
            const beamMaterial = new THREE.MeshBasicMaterial({
                color: 0xFC3F3F,
                transparent: true,
                opacity: 0.2,
                side: THREE.DoubleSide
            });
            const beam = new THREE.Mesh(beamGeometry, beamMaterial);
            beam.position.y = 3;
            hologramGroup.add(beam);

            // Particles around the beam
            const particlesGeometry = new THREE.BufferGeometry();
            const particleCount = 100;
            const positions = new Float32Array(particleCount * 3);

            for (let i = 0; i < particleCount; i++) {
                const angle = Math.random() * Math.PI * 2;
                const radius = 0.5 + Math.random() * 1.5;
                const height = Math.random() * 6;

                positions[i * 3] = Math.cos(angle) * radius;
                positions[i * 3 + 1] = height;
                positions[i * 3 + 2] = Math.sin(angle) * radius;
            }

            particlesGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

            const particlesMaterial = new THREE.PointsMaterial({
                color: 0xFC3F3F,
                size: 0.1,
                transparent: true,
                opacity: 0.8
            });

            const particles = new THREE.Points(particlesGeometry, particlesMaterial);
            hologramGroup.add(particles);

            // Create ZoneX text
            const textMaterial = new THREE.MeshBasicMaterial({
                color: 0xFC3F3F,
                transparent: true,
                opacity: 0.9
            });

            // Since we can't load fonts easily, create a simple representation
            const zoneGeometry = new THREE.BoxGeometry(2, 0.5, 0.1);
            const zone = new THREE.Mesh(zoneGeometry, textMaterial);
            zone.position.y = 3;

            const xGeometry = new THREE.BoxGeometry(0.5, 0.5, 0.1);
            const x1 = new THREE.Mesh(xGeometry, textMaterial);
            x1.position.set(1.5, 3, 0);
            x1.rotation.z = Math.PI / 4;

            const x2 = new THREE.Mesh(xGeometry, textMaterial);
            x2.position.set(1.5, 3, 0);
            x2.rotation.z = -Math.PI / 4;

            hologramGroup.add(zone, x1, x2);

            return hologramGroup;
        };

        const hologram = createHologram();
        hologram.position.set(0, 0, 0);
        cityGroup.add(hologram);

        scene.add(cityGroup);

        // Animation
        let time = 0;
        function animate() {
            requestAnimationFrame(animate);

            time += 0.01;

            // Rotate camera around scene
            camera.position.x = Math.sin(time * 0.2) * 15;
            camera.position.z = Math.cos(time * 0.2) * 15;
            camera.lookAt(0, 2, 0);

            // Animate cars
            cars.forEach(car => {
                if (car.onVerticalRoad) {
                    // Move along z axis
                    if (Math.abs(car.mesh.rotation.y) < 0.1) {
                        car.mesh.position.z += car.speed;
                        if (car.mesh.position.z > 40) {
                            car.mesh.position.z = -40;
                        }
                    } else {
                        car.mesh.position.z -= car.speed;
                        if (car.mesh.position.z < -40) {
                            car.mesh.position.z = 40;
                        }
                    }
                } else {
                    // Move along x axis
                    if (Math.abs(car.mesh.rotation.y - Math.PI / 2) < 0.1) {
                        car.mesh.position.x += car.speed;
                        if (car.mesh.position.x > 40) {
                            car.mesh.position.x = -40;
                        }
                    } else {
                        car.mesh.position.x -= car.speed;
                        if (car.mesh.position.x < -40) {
                            car.mesh.position.x = 40;
                        }
                    }
                }
            });

            // Animate hologram
            hologram.rotation.y += 0.01;

            // Update particles
            const particles = hologram.children[2];
            const positions = particles.geometry.attributes.position.array;

            for (let i = 0; i < positions.length; i += 3) {
                positions[i + 1] += 0.02;
                if (positions[i + 1] > 6) {
                    positions[i + 1] = 0;
                }
            }

            particles.geometry.attributes.position.needsUpdate = true;

            renderer.render(scene, camera);
        }

        animate();

        // Handle window resize
        const handleResize = () => {
            if (!canvasRef.current) return;

            camera.aspect = canvasRef.current.clientWidth / canvasRef.current.clientHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(canvasRef.current.clientWidth, canvasRef.current.clientHeight);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (canvasRef.current) {
                canvasRef.current.removeChild(renderer.domElement);
            }
            renderer.dispose();
        };
    }, []);

    return <div ref={canvasRef} className="w-full h-full"></div>;
};

const AboutPage = () => {
    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto'
        });
        
        // Additional fallback methods
        document.body.scrollTop = 0; // For Safari
        document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE, and Opera
    }, []);
    
    return (
        <div className="min-h-screen bg-zonex-white text-zonex-gray-900">
            {/* Hero Section */}
            <section className="py-28 bg-zonex-white relative overflow-hidden">
                {/* Red accent elements */}
                <div className="absolute inset-0 z-0">
                    <div className="absolute inset-0 bg-gradient-to-tr from-zonex-red/5 via-transparent to-transparent z-0"></div>
                    <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-zonex-red/10 rounded-full blur-3xl animate-float-slow"></div>
                    <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-zonex-red/15 rounded-full blur-3xl animate-float-slow-reverse"></div>
                    <div className="absolute top-1/3 right-1/3 w-24 h-24 bg-zonex-red/10 rounded-full blur-3xl animate-float-slow-alt"></div>
                    <div className="absolute inset-0 bg-gradient-to-tr from-zonex-red/5 via-transparent to-transparent z-0"></div>
                </div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center max-w-4xl mx-auto">
                        <div className="mb-8">
                            <ZoneXLogo3D containerHeight="250px" containerWidth="450px" />
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold mb-6">
                            Revolutionizing <span className="text-zonex-red">Mobility</span> & Transportation
                        </h1>
                        <div className="w-20 h-1 bg-zonex-red mx-auto mb-8 rounded-full shadow-red-glow"></div>
                        <p className="text-lg md:text-xl text-zonex-gray-700 mb-8">
                            ZoneX is on a mission to transform how people interact with urban transport infrastructure,
                            making parking, vehicle maintenance, and mobility services seamless, efficient, and stress-free.
                        </p>
                    </div>
                </div>

                {/* Animated Red Particles */}
                {/* <AboutParticles /> */}
            </section>

            {/* Our Story Section */}
            <section className="py-20 bg-zonex-darkGray relative overflow-hidden">
                {/* Red accent shapes */}
                <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-zonex-red/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-zonex-red/5 rounded-full blur-3xl"></div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Story</h2>
                            <div className="w-16 h-1 bg-zonex-red mb-6 rounded-full shadow-red-glow "></div>
                            <p className="text-zonex-gray-700 mb-6">
                                Founded in 2022, ZoneX emerged from a simple observation: finding parking in urban areas
                                was unnecessarily stressful and time-consuming. What began as a solution to simplify parking
                                has evolved into a comprehensive mobility platform.
                            </p>
                            <p className="text-zonex-gray-700 mb-6">
                                Our team of innovators recognized that urban mobility challenges extended beyond just finding
                                a parking spot. Vehicle maintenance, spare parts accessibility, and efficient transportation
                                solutions were all interconnected aspects of the modern driver's experience.
                            </p>
                            <p className="text-zonex-gray-700">
                                Today, ZoneX stands at the intersection of cutting-edge technology and practical mobility solutions,
                                serving thousands of users daily across multiple cities. Our integrated approach combines real-time
                                data, user-focused design, and strategic partnerships to create an ecosystem that simplifies every
                                aspect of vehicle ownership and urban mobility.
                            </p>
                        </div>
                        <div className="relative">
                            <div className="bg-black rounded-xl overflow-hidden border border-zonex-red/30 p-1 shadow-red-lg">
                                <div className="relative aspect-video overflow-hidden rounded-lg">
                                    <TimelineAnimation />
                                </div>
                            </div>
                            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-zonex-red/20 rounded-full blur-3xl animate-pulse-slow"></div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Our Values Section */}
            <section className="py-20 bg-zonex-white relative overflow-hidden">
                {/* Enhanced accent elements */}
                <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-zonex-red/15 rounded-full blur-3xl animate-float-slow"></div>
                <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-zonex-red/10 rounded-full blur-3xl animate-float-slow-reverse"></div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-zonex-gray-900">Our Core Values</h2>
                        <div className="w-20 h-1 bg-zonex-red mx-auto mb-6 rounded-full shadow-red-glow"></div>
                        <p className="text-zonex-gray-700 max-w-2xl mx-auto">
                            The principles that guide everything we do at ZoneX
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[
                            {
                                icon: <Brain className="w-8 h-8 text-zonex-red" />, // Replaced Zap with Brain
                                title: "Innovation",
                                description: "We continuously push boundaries, reimagining what's possible in urban mobility and vehicle services."
                            },
                            {
                                icon: <Users className="w-8 h-8 text-zonex-red" />,
                                title: "User-Centered",
                                description: "Every feature and service is designed with our users' needs, comfort, and experience at the forefront."
                            },
                          {
                                icon: <Award className="w-8 h-8 text-zonex-red" />,
                                title: "Excellence",
                                description: "We hold ourselves to the highest standards in technology, service, and customer experience."
                            },
                            {
                                icon: <Globe className="w-8 h-8 text-zonex-red" />,
                                title: "Sustainability",
                                description: "Our solutions aim to reduce urban congestion and contribute to more sustainable cities."
                            },
                            {
                                icon: <Target className="w-8 h-8 text-zonex-red" />,
                                title: "Accessibility",
                                description: "We believe efficient mobility solutions should be available and accessible to everyone."
                            },
                            {
                                icon: <Calendar className="w-8 h-8 text-zonex-red" />,
                                title: "Reliability",
                                description: "Our users trust us with their time and vehicles, a responsibility we take seriously every day."
                            }
                        ].map((value, index) => (
                            <div
                                key={index}
                                className={`bg-zonex-white p-8 rounded-xl border border-gray-200 hover:border-zonex-red/50 shadow-md hover:shadow-lg hover:-translate-y-2 transition-all duration-500 group ${
                                    index % 2 === 0 ? 'animate-fade-in-up' : 'animate-fade-in-down'
                                }`}
                                style={{ animationDelay: `${index * 100}ms` }}
                            >
                                <div className="p-4 bg-zonex-red/10 rounded-lg inline-block mb-6 group-hover:bg-zonex-red/20 transition-all duration-300">
                                    {value.icon}
                                </div>
                                <h3 className="text-xl font-semibold mb-4 text-zonex-gray-900">{value.title}</h3>
                                <p className="text-zonex-gray-700">{value.description}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Custom CSS */}
                <style jsx>{`
                    @keyframes float-slow {
                        0% { transform: translateY(0); }
                        50% { transform: translateY(-20px); }
                        100% { transform: translateY(0); }
                    }
                    
                    @keyframes float-slow-reverse {
                        0% { transform: translateY(0); }
                        50% { transform: translateY(20px); }
                        100% { transform: translateY(0); }
                    }
                    
                    @keyframes fade-in-up {
                        from { opacity: 0; transform: translateY(20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    
                    @keyframes fade-in-down {
                        from { opacity: 0; transform: translateY(-20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    
                    .animate-float-slow {
                        animation: float-slow 8s ease-in-out infinite;
                    }
                    
                    .animate-float-slow-reverse {
                        animation: float-slow-reverse 12s ease-in-out infinite;
                    }
                    
                    .animate-fade-in-up {
                        animation: fade-in-up 0.8s ease-out forwards;
                    }
                    
                    .animate-fade-in-down {
                        animation: fade-in-down 0.8s ease-out forwards;
                    }
                    
                    .shadow-red-glow {
                        box-shadow: 0 0 15px rgba(252, 63, 63, 0.4);
                    }
                    
                    .shadow-red-lg {
                        box-shadow: 0 10px 15px -3px rgba(252, 63, 63, 0.1), 0 4px 6px -2px rgba(252, 63, 63, 0.05);
                    }
                `}</style>
            </section>

            {/* Vision for the Future */}
            <section className="py-20 bg-zonex-darkGray relative overflow-hidden">
                <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-zonex-red/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-zonex-red/5 rounded-full blur-3xl"></div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div className="order-2 lg:order-1">
                            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Vision for the Future</h2>
                            <div className="w-16 h-1 bg-zonex-red mb-6 rounded-full shadow-red-glow"></div>
                            <p className="text-zonex-gray-700 mb-6">
                                At ZoneX, we envision a future where urban mobility is seamless, efficient, and sustainable.
                                Our roadmap includes expanding to 20+ cities by 2026, integrating with smart city
                                infrastructure, and pioneering AI-driven predictive systems for parking and maintenance.
                            </p>
                            <p className="text-zonex-gray-700 mb-6">
                                We're developing technologies that will allow vehicles to communicate directly with
                                parking systems, mechanics to diagnose issues remotely, and users to experience truly
                                frictionless urban transportation.
                            </p>
                            <p className="text-zonex-gray-700">
                                Beyond our current offerings, we're exploring partnerships with electric vehicle
                                manufacturers, automated parking solutions, and public transportation networks to
                                create a holistic ecosystem for the future of mobility.
                            </p>
                        </div>
                        <div className="order-1 lg:order-2 relative">
                            <div className="bg-black rounded-xl overflow-hidden border border-zonex-red/30 shadow-red-lg w-full aspect-square max-w-md mx-auto">
                                <VisionAnimation />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-28 bg-zonex-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-zonex-red/10 to-transparent opacity-50"></div>
                <div className="absolute inset-0 bg-gradient-to-tr from-zonex-red/5 via-transparent to-transparent z-0"></div>
                <div className="absolute inset-0 bg-gradient-to-tr from-zonex-red/5 via-transparent to-transparent z-0"></div>
                <div className="absolute inset-0 bg-gradient-to-tr from-zonex-red/5 via-transparent to-transparent z-0"></div>
                <div className="container mx-auto px-4 relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-3xl md:text-5xl font-bold mb-6">Join Us on This Journey</h2>
                        <p className="text-xl text-zonex-gray-700 mb-8">
                            Whether you're a user, partner, or potential team member, we invite you to be part of
                            revolutionizing urban mobility with ZoneX.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a href="/contact" className="bg-zonex-red text-white px-8 py-3 rounded-full hover:bg-zonex-darkRed transition-all duration-300 flex items-center justify-center shadow-red-md">
                                Contact Us <ChevronRight className="w-5 h-5 ml-2" />
                            </a>
                            <a href="/services" className="border border-zonex-red/50 text-black px-8 py-3 rounded-full hover:bg-zonex-red/10 transition-all duration-300 flex items-center justify-center">
                                Explore Our Services
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Animation Styles */}
            <style jsx>{`
                @keyframes float-slow {
                    0% { transform: translateY(0) rotate(0); }
                    50% { transform: translateY(-20px) rotate(5deg); }
                    100% { transform: translateY(0) rotate(0); }
                }
                
                @keyframes float-slow-reverse {
                    0% { transform: translateY(0) rotate(0); }
                    50% { transform: translateY(20px) rotate(-5deg); }
                    100% { transform: translateY(0) rotate(0); }
                }
                
                @keyframes float-slow-alt {
                    0% { transform: translateY(0) rotate(0); }
                    50% { transform: translateY(-15px) rotate(-3deg); }
                    100% { transform: translateY(0) rotate(0); }
                }
                
                @keyframes pulse-slow {
                    0% { opacity: 0.3; }
                    50% { opacity: 0.6; }
                    100% { opacity: 0.3; }
                }
                
                .animate-float-slow {
                    animation: float-slow 8s ease-in-out infinite;
                }
                
                .animate-float-slow-reverse {
                    animation: float-slow-reverse 12s ease-in-out infinite;
                }
                
                .animate-float-slow-alt {
                    animation: float-slow-alt 10s ease-in-out infinite;
                }
                
                .animate-pulse-slow {
                    animation: pulse-slow 4s ease-in-out infinite;
                }
                
                .shadow-red-glow {
                    box-shadow: 0 0 15px rgba(252, 63, 63, 0.4);
                }
                
                .shadow-red-md {
                    box-shadow: 0 4px 6px -1px rgba(252, 63, 63, 0.1), 0 2px 4px -1px rgba(252, 63, 63, 0.06);
                }
                
                .shadow-red-lg {
                    box-shadow: 0 10px 15px -3px rgba(252, 63, 63, 0.1), 0 4px 6px -2px rgba(252, 63, 63, 0.05);
                }
                
                .shadow-red-sm {
                    box-shadow: 0 1px 2px 0 rgba(252, 63, 63, 0.05);
                }
            `}</style>
        </div>
    );
};

export default AboutPage;